import React, { useRef, useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { DayPicker } from "react-day-picker";
import "react-day-picker/style.css";
import ReserveOptions from "./ReserveOptions";
import { getBlockedDates } from "../lib/availability";
import { trackEvent, trackConversion } from "../lib/analytics";

// Parse a "YYYY-MM-DD" string as a LOCAL date (avoids a UTC off-by-one in the calendar)
const parseLocalDate = (s) => {
  const [y, m, d] = String(s).split("-").map(Number);
  return new Date(y, m - 1, d);
};

const menuOptions = [
  {
    category: "restaurationFull.categories.breakfast",
    items: [
      { 
        id: "moroccanBreakfast", 
        name: "restaurationFull.items.moroccanBreakfast", 
        price: "40 MAD",
        perPerson: true
      }
    ]
  },
  {
    category: "restaurationFull.categories.lunch",
    items: [
      { 
        id: "meatTajine-quarter", 
        name: "restaurationFull.items.meatTajine", 
        description: "1/4 kg",
        price: "60 MAD"
      },
      { 
        id: "meatTajine-half", 
        name: "restaurationFull.items.meatTajine", 
        description: "0.5 kg",
        price: "90 MAD"
      },
      { 
        id: "meatTajine-full", 
        name: "restaurationFull.items.meatTajine", 
        description: "1 kg",
        price: "150 MAD"
      },
      { 
        id: "chickenTajine-quarter", 
        name: "restaurationFull.items.chickenTajine", 
        description: "1/4 kg",
        price: "60 MAD"
      },
      { 
        id: "chickenTajine-half", 
        name: "restaurationFull.items.chickenTajine", 
        description: "0.5 kg",
        price: "90 MAD"
      },
      { 
        id: "chickenTajine-full", 
        name: "restaurationFull.items.chickenTajine", 
        description: "1 kg",
        price: "150 MAD"
      }
    ]
  },
  {
    category: "restaurationFull.categories.seasonal",
    items: [
      { 
        id: "seasonalFruit", 
        name: "restaurationFull.items.seasonalFruit", 
        price: "40 MAD"
      }
    ]
  },
  {
    category: "restaurationFull.categories.beverages",
    items: [
      {
        id: "mintTea",
        name: "restaurationFull.items.mintTea",
        price: "20 MAD"
      },
      {
        id: "sparklingWater",
        name: "restaurationFull.items.sparklingWater",
        price: "15 MAD"
      },
      {
        id: "otherBeverages",
        name: "restaurationFull.items.otherBeverages",
        price: "restaurationFull.prices.otherBeverages"
      }
    ]
  }
];

const Booking = () => {
  const form = useRef();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedMeals, setSelectedMeals] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showMenu, setShowMenu] = useState(false);
  const [range, setRange] = useState();
  const [blockedRanges, setBlockedRanges] = useState([]);
  const { t } = useTranslation();

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Load blocked/booked dates so the calendar can disable them
  useEffect(() => {
    getBlockedDates().then((rows) => {
      setBlockedRanges(
        rows
          .filter((r) => r.start_date && r.end_date)
          .map((r) => ({
            from: parseLocalDate(r.start_date),
            to: parseLocalDate(r.end_date),
          }))
      );
    });
  }, []);

  const fmtDate = (d) =>
    d
      ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
          d.getDate()
        ).padStart(2, "0")}`
      : "";

  const disabledDays = [{ before: today }, ...blockedRanges];

  // Rooms array with useMemo to re-translate when language changes
  const rooms = useMemo(() => [
    {
      id: "atlas-suite",
      name: t('booking.atlasSuite'),
      shortDescription: t('booking.atlasSuiteDesc'),
      fullDescription: t('booking.atlasSuiteFull'),
      price: "700 MAD/night",
      features: [
        t('booking.kingBed'),
        t('booking.panoramicView'),
        t('booking.privateTerrace'),
        t('booking.ensuiteBathroom'),
        t('booking.airConditioning')
      ],
      amenities: [
        t('booking.freeWifi'),
        t('booking.tv'),
        t('booking.minifridge'),
        t('booking.hairdryer'),
        t('booking.bathrobes'),
        t('booking.safe'),
      ],
      images: [
        "/images/gallery/bed.jpg",
        "/images/gallery/atlas1.jpg",
        "/images/gallery/atlas2.jpg",
        "/images/gallery/photo3.jpg"
      ],
      capacity: 2,
      available: true
    },
    {
      id: "berber-room",
      name: t('booking.berberRoom'),
      shortDescription: t('booking.berberRoomDesc'),
      fullDescription: t('booking.berberRoomFull'),
      price: "600 MAD/night",
      features: [
        t('booking.queenBed'),
        t('booking.gardenView'),
        t('booking.traditionalDecor'),
        t('booking.ensuiteBathroom'),
        t('booking.kitchen'),
        t('booking.airConditioning'),
      ],
      amenities: [
        t('booking.freeWifi'),
        t('booking.smartTV'),
        t('booking.teaCoffeeMaker'),
        t('booking.hairdryer')
      ],
      images: [
        "/images/gallery/cave.jpg",
        "/images/gallery/cave2.jpg",
        "/images/gallery/kitchen2.jpg"
      ],
      capacity: 2,
      available: true
    },
    {
      id: "ourika-mountain-combo",
      name: t('booking.comboSuite'),
      shortDescription: t('booking.comboSuiteDesc'),
      fullDescription: t('booking.comboSuiteFull'),
      price: "1000 MAD/night",
      features: [
        t('booking.twoDoubleBeds'),
        t('booking.ensuiteBathroom'),
        t('booking.mountainView'),
        t('booking.twoBathrooms'),
        t('booking.airConditioning'),
        t('booking.familyFriendly')
      ],
      amenities: [
        t('booking.freeWifi'),
        t('booking.smartTV'),
        t('booking.teaCoffeeMaker'),
        t('booking.twoBathrooms'),
        t('booking.familySpace')
      ],
      images: [
        "/images/gallery/uproom.jpg",
        "/images/gallery/uproom2-.jpg",
        "/images/gallery/uproom3.jpg",
        "/images/gallery/uproom4.jpg",
        "/images/gallery/uproom2.jpg",
      ],
      capacity: 4,
      available: true,
      isCombo: true
    },
    {
      id: "ourika-room",
      name: t('booking.ourikaRoom'),
      shortDescription: t('booking.ourikaRoomDesc'),
      fullDescription: t('booking.ourikaRoomFull'),
      price: "500 MAD/night",
      features: [
        t('booking.doubleBed'),
        t('booking.valleyView'),
        t('booking.modernAmenities'),
        t('booking.ensuiteBathroom'),
        t('booking.airConditioning'),
      ],
      amenities: [
        t('booking.freeWifi'),
        t('booking.teaCoffeeMaker'),
        t('booking.valleyView'),
        t('booking.smartTV'),
      ],
      images: [
        "/images/gallery/uproom.jpg",
        "/images/gallery/chairs.jpg",
      ],
      capacity: 2,
      available: true
    },
    {
      id: "mountain-view",
      name: t('booking.mountainRoom'),
      shortDescription: t('booking.mountainRoomDesc'),
      fullDescription: t('booking.mountainRoomFull'),
      price: "500 MAD/night",
      features: [
        t('booking.doubleBed'),
        t('booking.mountainView'),
        t('booking.traditionalStyle'),
        t('booking.airConditioning'),
        t('booking.seatingArea')
      ],
      amenities: [
        t('booking.freeWifi'),
        t('booking.teaCoffeeMaker'),
        t('booking.mountainView'),
        t('booking.ensuiteBathroom')
      ],
      images: [
        "/images/gallery/uproom2-.jpg",
        "/images/gallery/room2.jpg",
        "/images/gallery/room3.jpg"
      ],
      capacity: 2,
      available: true
    },
    {
      id: "garden-room",
      name: t('booking.gardenRoom'),
      shortDescription: t('booking.gardenRoomDesc'),
      fullDescription: t('booking.gardenRoomFull'),
      price: "600 MAD/night",
      features: [
        t('booking.twinBeds'),
        t('booking.gardenAccess'),
        t('booking.groundFloor'),
        t('booking.ensuiteBathroom'),
        t('booking.minifridge'),
        t('booking.airConditioning'),
        t('booking.gardenView')
      ],
      amenities: [
        t('booking.freeWifi'),
        t('booking.gardenAccess'),
        t('booking.outdoorSeating'),
        t('booking.teaCoffeeMaker'),
      ],
      images: [
        "/images/gallery/outside.jpg",
        "/images/gallery/outside2.jpg",
        "/images/gallery/outside3.jpg"
      ],
      capacity: 2,
      available: true
    }
  ], [t]); // Add t as dependency to re-create rooms when language changes

  const handleMealSelection = (mealId, isSelected) => {
    setSelectedMeals(prev => ({
      ...prev,
      [mealId]: isSelected
    }));
  };

  const toggleMenu = () => {
    setShowMenu(!showMenu);
  };

  const nextImage = () => {
    setCurrentImageIndex(prev => 
      prev === selectedRoom.images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex(prev => 
      prev === 0 ? selectedRoom.images.length - 1 : prev - 1
    );
  };

  const selectRoom = (roomId) => {
    setSelectedRoom(rooms.find(room => room.id === roomId));
    setCurrentStep(2);
    setCurrentImageIndex(0);
  };

  const goToBooking = () => {
    setCurrentStep(3);
  };

  const goBackToSelection = () => {
    setCurrentStep(1);
    setSelectedRoom(null);
  };

  const goBackToDetails = () => {
    setCurrentStep(2);
  };

  const [loading, setLoading] = useState(false);

  const sendBooking = async (e) => {
    e.preventDefault();

    const fd = new FormData(form.current);
    // Honeypot: silently ignore bot submissions
    if (fd.get("company")) return;
    // Require a date range; check-out must be after check-in
    const ci = fd.get("check_in");
    const co = fd.get("check_out");
    if (!ci || !co || new Date(co) <= new Date(ci)) {
      setError(true);
      return;
    }

    setLoading(true);

    const selectedMealsText = Object.keys(selectedMeals)
      .filter(mealId => selectedMeals[mealId])
      .map(mealId => {
        const meal = menuOptions.flatMap(cat => cat.items).find(item => item.id === mealId);
        return meal ? `${t(meal.name)} - ${meal.price}` : "";
      })
      .filter(Boolean)
      .join(", ");

    const formData = new FormData(form.current);

    try {
      const res = await fetch("/api/booking", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_name: formData.get("user_name"),
          user_email: formData.get("user_email"),
          user_phone: formData.get("user_phone"),
          guests: formData.get("guests"),
          check_in: formData.get("check_in"),
          check_out: formData.get("check_out"),
          room_id: selectedRoom ? selectedRoom.id : null,
          selected_room: selectedRoom ? selectedRoom.name : "",
          selected_meals: selectedMealsText || t('booking.noMeals'),
          additional_requests: formData.get("additional_requests"),
        }),
      });

      if (res.ok) {
        setSubmitted(true);
        setError(false);
        trackEvent("booking_request", {
          room: selectedRoom ? selectedRoom.name : "",
        });
        trackConversion();
        form.current.reset();
        setSelectedMeals({});
        setRange(undefined);
      } else {
        setError(true);
      }
    } catch (err) {
      console.error("Booking error:", err.message);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  // Progress Steps Component
  const ProgressSteps = () => (
    <div className="flex justify-center mb-12">
      <div className="flex items-center space-x-4">
        {[1, 2, 3].map((step) => (
          <div key={step} className="flex items-center">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
              currentStep >= step 
                ? 'bg-brass border-brass text-white' 
                : 'border-gray-300 text-gray-400'
            } font-medium`}>
              {step}
            </div>
            {step < 3 && (
              <div className={`w-16 h-0.5 ${
                currentStep > step ? 'bg-brass' : 'bg-gray-300'
              }`} />
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <section id="booking" className="pt-32 md:pt-40 pb-24 bg-cream">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        {/* Reserve on OTA platforms */}
        <ReserveOptions />
        <div className="my-14 md:my-16 flex items-center gap-4 max-w-3xl mx-auto">
          <span className="flex-1 h-px bg-ink/10" />
          <span className="eyebrow text-ink-muted whitespace-nowrap">
            {t('reserveOptions.orDirect')}
          </span>
          <span className="flex-1 h-px bg-ink/10" />
        </div>
        {/* Luxury Header */}
        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.h2
            className="text-4xl md:text-5xl lg:text-6xl font-light text-ink mb-6 tracking-tight"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            {currentStep === 1 && t('booking.selectRoom')}
            {currentStep === 2 && t('booking.roomDetails')}
            {currentStep === 3 && t('booking.completeBooking')}
          </motion.h2>
          <motion.div 
            className="w-24 h-0.5 bg-brass mx-auto mb-8"
            initial={{ width: 0 }}
            whileInView={{ width: 96 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.8 }}
          />
          
          {/* Progress Steps */}
          <ProgressSteps />
        </motion.div>

        {/* Status Messages */}
        {submitted && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-6 bg-green-50 border border-green-200 rounded-2xl text-green-800 text-center"
          >
            <div className="text-lg font-medium mb-2">{t('booking.successTitle')}</div>
            <p className="text-green-700">{t('booking.successMessage')}</p>
          </motion.div>
        )}
        
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-6 bg-red-50 border border-red-200 rounded-2xl text-red-800 text-center"
          >
            <div className="text-lg font-medium mb-2">{t('booking.errorTitle')}</div>
            <p className="text-red-700">{t('booking.errorMessage')}</p>
          </motion.div>
        )}

        <AnimatePresence mode="wait">
          {/* STEP 1: Room Selection */}
          {currentStep === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {rooms.map((room, index) => (
                  <motion.div
                    key={room.id}
                    className={`group bg-cream border overflow-hidden cursor-pointer transition-all duration-500 hover:shadow-[0_20px_50px_rgba(28,27,24,0.10)] ${
                      room.isCombo ? 'border-brass' : 'border-ink/10 hover:border-brass'
                    }`}
                    whileHover={{ y: -6 }}
                    onClick={() => selectRoom(room.id)}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.08, duration: 0.6 }}
                  >
                    <div className="relative overflow-hidden aspect-[4/3] bg-cream-dark">
                      <img
                        src={room.images[0]}
                        alt={room.name}
                        className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                        loading="lazy"
                        decoding="async"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-ink/10 to-transparent" />
                      {room.isCombo && (
                        <div className="absolute top-4 left-4 bg-brass text-ink text-[10px] font-medium py-1 px-3 uppercase tracking-[0.15em]">
                          {t('booking.comboDeal')}
                        </div>
                      )}
                      <div className="absolute top-4 right-4 bg-cream/90 text-ink text-xs font-medium py-1 px-3 tracking-wide">
                        {room.price}
                      </div>
                      <h3 className="absolute bottom-4 left-5 right-5 font-serif text-2xl font-light text-cream leading-tight">
                        {room.name}
                      </h3>
                    </div>

                    <div className="p-6">
                      <p className="text-ink-soft text-sm mb-5 leading-relaxed">{room.shortDescription}</p>
                      <div className="flex items-center justify-between border-t border-ink/10 pt-4">
                        <span className="text-ink-muted text-sm tracking-wide">
                          {room.capacity} {t('booking.guests')}
                        </span>
                        <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-brass">
                          {t('booking.bookRoom')}
                          <svg className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                          </svg>
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* STEP 2: Room Details Gallery */}
          {currentStep === 2 && selectedRoom && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.5 }}
              className="space-y-8"
            >
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <div className="relative bg-gray-100 min-h-[400px] flex items-center justify-center">
                  <img
                    src={selectedRoom.images[currentImageIndex]}
                    alt={selectedRoom.name}
                    className="max-w-full max-h-[500px] w-auto h-auto object-scale-down"
                  />
                  
                  {selectedRoom.images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-3 transition-all duration-300"
                      >
                        ←
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-3 transition-all duration-300"
                      >
                        →
                      </button>
                    </>
                  )}
                  
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                    {currentImageIndex + 1} / {selectedRoom.images.length}
                  </div>
                </div>
              </div>

              <div className="grid lg:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-3xl font-light text-ink mb-2">{selectedRoom.name}</h3>
                    <p className="text-brass text-2xl font-medium mb-4">{selectedRoom.price}</p>
                    <p className="text-gray-600 leading-relaxed">{selectedRoom.fullDescription}</p>
                  </div>

                  <div>
                    <h4 className="text-xl font-light text-ink mb-4">{t('booking.roomFeatures')}</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {selectedRoom.features.map((feature, index) => (
                        <div key={index} className="flex items-center space-x-3">
                          <div className="w-2 h-2 bg-brass rounded-full"></div>
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <h4 className="text-xl font-light text-ink mb-4">{t('booking.amenities')}</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {selectedRoom.amenities.map((amenity, index) => (
                        <div key={index} className="flex items-center space-x-3">
                          <div className="w-2 h-2 bg-brass rounded-full"></div>
                          <span className="text-gray-700">{amenity}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-cream-dark border border-brass/40 rounded-2xl p-6">
                    <div className="text-center space-y-4">
                      <div className="text-2xl font-light text-ink">{selectedRoom.price}</div>
                      <div className="text-gray-600">{t('booking.perfectFor')} {selectedRoom.capacity} {t('booking.guests')}</div>
                      <motion.button
                        onClick={goToBooking}
                        className="w-full bg-brass hover:bg-brass-dark text-white font-medium py-3 px-8 rounded-lg transition-all duration-300"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {t('booking.bookRoom')}
                      </motion.button>
                      <button
                        onClick={goBackToSelection}
                        className="text-gray-600 hover:text-gray-800 transition-colors duration-300"
                      >
                        ← {t('booking.backToRooms')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* STEP 3: Booking Form */}
          {currentStep === 3 && selectedRoom && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.5 }}
            >
              <motion.div
                className="bg-gradient-to-r from-cream-dark to-white border border-brass/40 rounded-2xl p-6 mb-8"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-6">
                    <img
                      src={selectedRoom.images[0]}
                      alt={selectedRoom.name}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                    <div>
                      <h3 className="text-2xl font-light text-ink mb-1">{selectedRoom.name}</h3>
                      <p className="text-brass text-lg font-medium">{selectedRoom.price}</p>
                    </div>
                  </div>
                  <button
                    onClick={goBackToDetails}
                    className="text-gray-500 hover:text-gray-700 transition-colors duration-300"
                  >
                    {t('booking.changeRoom')}
                  </button>
                </div>
              </motion.div>

              <motion.form
                ref={form}
                onSubmit={sendBooking}
                className="bg-gray-50 rounded-3xl shadow-sm border border-gray-100 p-8 md:p-12 space-y-8"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <div className="space-y-8">
                  <h3 className="text-2xl font-light text-ink border-b border-gray-200 pb-4">{t('booking.guestInfo')}</h3>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-gray-700 mb-3 font-medium">{t('booking.fullName')} *</label>
                      <input 
                        type="text" 
                        name="user_name" 
                        required 
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brass focus:border-transparent transition-all duration-300"
                        placeholder={t('booking.namePlaceholder')}
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 mb-3 font-medium">{t('booking.email')} *</label>
                      <input 
                        type="email" 
                        name="user_email" 
                        required 
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brass focus:border-transparent transition-all duration-300"
                        placeholder={t('booking.emailPlaceholder')}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-gray-700 mb-3 font-medium">{t('booking.phone')}</label>
                      <input 
                        type="tel" 
                        name="user_phone" 
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brass focus:border-transparent transition-all duration-300"
                        placeholder={t('booking.phonePlaceholder')}
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 mb-3 font-medium">{t('booking.guests')} *</label>
                      <select 
                        name="guests" 
                        required 
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brass focus:border-transparent transition-all duration-300"
                      >
                        <option value="">{t('booking.selectGuests')}</option>
                        <option value="1">1 {t('booking.guest')}</option>
                        <option value="2">2 {t('booking.guests')}</option>
                        <option value="3">3 {t('booking.guests')}</option>
                        <option value="4">4 {t('booking.guests')}</option>
                        <option value="5+">5+ {t('booking.guests')}</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-700 mb-3 font-medium">
                      {t('booking.checkIn')} — {t('booking.checkOut')} *
                    </label>
                    <div className="border border-gray-300 rounded-lg p-4 bg-white inline-block">
                      <DayPicker
                        mode="range"
                        numberOfMonths={1}
                        weekStartsOn={1}
                        selected={range}
                        onSelect={setRange}
                        disabled={disabledDays}
                        modifiers={{ blocked: blockedRanges }}
                        modifiersClassNames={{ blocked: "day-blocked" }}
                      />
                    </div>
                    {range?.from && (
                      <p className="text-sm text-ink-soft mt-3">
                        {fmtDate(range.from)}
                        {range.to ? ` → ${fmtDate(range.to)}` : ""}
                      </p>
                    )}
                    <p className="text-xs text-ink-muted mt-2">
                      {t("booking.unavailableNote")}
                    </p>
                    <input type="hidden" name="check_in" value={fmtDate(range?.from)} readOnly />
                    <input type="hidden" name="check_out" value={fmtDate(range?.to)} readOnly />
                  </div>

                  {/* Honeypot anti-spam field (hidden from real users) */}
                  <input
                    type="text"
                    name="company"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="hidden"
                  />

                  {/* Optional Meal Selection Section */}
                  <div className="border border-gray-200 rounded-xl p-6">
                    <button
                      type="button"
                      onClick={toggleMenu}
                      className="flex items-center justify-between w-full text-left focus:outline-none focus:ring-2 focus:ring-brass focus:ring-opacity-50 rounded-lg p-2"
                    >
                      <div>
                        <h4 className="text-xl font-light text-ink">{t('booking.mealSelection')}</h4>
                        <p className="text-gray-600 text-sm mt-1">
                          {t('booking.mealSelectionOptional')}
                        </p>
                      </div>
                      <motion.div
                        animate={{ rotate: showMenu ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                        className="text-brass"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </motion.div>
                    </button>

                    <AnimatePresence>
                      {showMenu && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="mt-6 space-y-6 border-t border-gray-200 pt-6">
                            {menuOptions.map((category) => (
                              <div key={category.category} className="border border-gray-200 rounded-xl p-6">
                                <h5 className="text-lg font-medium text-ink mb-4">{t(category.category)}</h5>
                                <div className="space-y-3">
                                  {category.items.map((item) => (
                                    <label key={item.id} className="flex items-center justify-between p-3 hover:bg-cream-dark rounded-lg cursor-pointer transition-colors duration-200">
                                      <div className="flex items-center space-x-4">
                                        <input
                                          type="checkbox"
                                          checked={selectedMeals[item.id] || false}
                                          onChange={(e) => handleMealSelection(item.id, e.target.checked)}
                                          className="w-4 h-4 text-brass border-gray-300 rounded focus:ring-brass"
                                        />
                                        <div>
                                          <span className="text-gray-700 block">{t(item.name)}</span>
                                          {item.description && (
                                            <span className="text-gray-500 text-sm">{item.description}</span>
                                          )}
                                          {item.perPerson && (
                                            <span className="text-brass text-xs font-medium mt-1">
                                              {t('restaurationFull.perPerson')}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      <span className="text-brass font-medium">
                                        {item.price.includes('restaurationFull.prices') ? t(item.price) : item.price}
                                      </span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Additional Requests */}
                  <div>
                    <label className="block text-gray-700 mb-3 font-medium">{t('booking.additionalRequests')}</label>
                    <textarea 
                      name="additional_requests" 
                      rows="4"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brass focus:border-transparent transition-all duration-300"
                      placeholder={t('booking.requestsPlaceholder')}
                    />
                  </div>

                  <input type="hidden" name="selected_room" />
                  <input type="hidden" name="selected_meals" />
                </div>

                <motion.button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-ink hover:bg-brass-dark disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-4 px-8 rounded-lg text-lg transition-all duration-500 border border-ink hover:border-brass-dark relative overflow-hidden group"
                  whileHover={loading ? {} : { scale: 1.02 }}
                  whileTap={loading ? {} : { scale: 0.98 }}
                >
                  <span className="relative z-10">
                    {loading ? "Sending..." : t('booking.submitBooking')}
                  </span>
                  {!loading && (
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-brass to-brass opacity-0 group-hover:opacity-100"
                      initial={false}
                      transition={{ duration: 0.4 }}
                    />
                  )}
                </motion.button>
              </motion.form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Booking;